<template>
  <button
    ref="episodeButton"
    :class="currentEpisode ? `${className} active` : className"
    :data-src="episodeUrl"
    :show-pemlink="episodePermlink"
    :disabled="isBuffering"
    :data-provider="provider"
    @click="handlePlay()"
  >
    <img
      v-if="!isBuffering"
      class="episode-icon"
      :class="{ play: !isPlaying }"
      :src="require('@/assets/icons/svg/' + buttonIcon)"
      alt="play button"
    />
    <Loading v-else/>
  </button>
</template>
<script>

import Loading from '@/components/ui/Loading.vue';
import {mapState} from "vuex";

export default {
  name: 'EpisodeButton',
  components: {
    Loading
  },
  props: {
    episodeUrl: {
      type: String,
      required: true
    },
    episodePermlink: {
      type: String,
      required: true
    },
    artist: {
      type: Object,
      required: true
    },
    podcast: {
      type: Object,
      default: null,
      required: false
    },
    episodeTitle: {
      type: String,
      required: true
    },
    className: {
      type: String,
      required: true
    },
    provider: {
      type: String,
      required: false,
      default: 'MIXCLOUD'
    },
    soundcloudUrl: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      isPlaying: false,
      isBuffering: false,
      currentPlaying: false,
      play: 'play-component-icon.svg',
      pause: 'pause-component-icon.svg',
      showUrl: null,
      currentId: null,
      widget: null
    };
  },

  computed: {
    ...mapState({
      buttonIcon() {
        return this.isPlaying ? this.pause : this.play;
      },
      getSuspend() {
        return this.$store.getters['audioPlayer/getSuspend'];
      },
      isProviderPlaying() {
        return this.$store.getters['audioPlayer/getAudioPlayerIsPlaying'];
      },
      getSoundcloudInstance() {
        return this.$store.getters['audioPlayer/getSoundcloudInstance'];
      },
      getCurrentEpisodeId() {
        return this.$store.getters['audioPlayer/getEpisodeUrl'];
      },
      getDestroy() {
        return this.$store.getters['audioPlayer/getDestroy'];
      },
      getProvider() {
        return this.$store.getters['audioPlayer/getProvider'];
      },
      getStreamPlaying() {
        return this.$store.getters['radioPlayer/getPlaying'];
      },
      currentEpisode() {
        return this.isPlaying
            ? this.$emit('current-playing', true)
            : this.$emit('current-playing', false);
      }
    })
  },

  watch: {
    getSuspend(value) {
      if (this.getProvider === 'MIXCLOUD' && this.episodeUrl === this.getCurrentEpisodeId) {
        this.isBuffering = !value;
        this.isPlaying = false;
      } else if (this.getProvider === 'MIXCLOUD' && this.episodeUrl !== this.getCurrentEpisodeId) {
        this.isBuffering = false;
        this.isPlaying = false;
      }
    },
    isProviderPlaying() {
      if (this.getProvider === 'MIXCLOUD') {
        this.mixcloudListener();
      }
      if (this.getProvider === 'SOUNDCLOUD') {
        this.soundcloudListener();
      }
    },
    getSoundcloudInstance() {
      this.soundcloudListener();
    },
    getDestroy() {
      this.isPlaying = false;
    },
    getProvider(provider) {
      this.isPlaying = false;
      if (provider === null) {
        this.isBuffering = false;
        this.isPlaying = false
      }
    },
    getStreamPlaying(value) {
      if (value){
       this.isBuffering = false;
       this.isPlaying = false;
       this.$store.commit('audioPlayer/setProvider', null);
      }
    }
  },

  mounted() {
    this.$nextTick(function () {
      this.handleAudioPlayerListeners();
    });
  },

  activated() {
    this.handleAudioPlayerListeners();
  },

  methods: {
    handleAudioPlayerListeners() {
      switch (this.getProvider) {
        case 'SOUNDCLOUD':
          this.soundcloudListener();
          break;
        case 'MIXCLOUD':
          this.mixcloudListener();
          break;
      }
    },
    mixcloudListener() {
      if (!this.getSuspend) {
        this.isBuffering = true;
        this.isPlaying = false;
        return;
      }
      if (this.isProviderPlaying && (this.episodeUrl === this.getCurrentEpisodeId)) {
        this.isPlaying = true;
        this.isBuffering = false;
      }
      if (!this.isProviderPlaying && (this.episodeUrl === this.getCurrentEpisodeId)) {
        this.isPlaying = false;
        this.isBuffering = false;
      }
    },
    soundcloudListener() {
      if (!this.getSoundcloudInstance) {
        this.isBuffering = true;
        return;
      }
      this.getSoundcloudInstance.SndWidget.widget.isPaused((e) => {
        if (e) {
          this.isPlaying = false;
          this.isBuffering = false;
        }
      })
      this.getSoundcloudInstance.SndWidget.onPlay(() => {
        if (this.getCurrentEpisodeId === this.episodeUrl) {
          this.isPlaying = true;
          this.isBuffering = false;
        }
      });
      this.getSoundcloudInstance.SndWidget.onPause(() => {
        if (this.getCurrentEpisodeId === this.episodeUrl) {
          this.isPlaying = false;
          this.isBuffering = false;
        }
      });
      this.getSoundcloudInstance.SndWidget.onLoading(() => {
        if (this.getCurrentEpisodeId === this.episodeUrl) {
          this.isPlaying = false;
          this.isBuffering = true;
        }
      });
      if (this.isProviderPlaying && (this.episodeUrl === this.getCurrentEpisodeId)) {
        this.isPlaying = true;
      }
      if (this.isProviderPlaying && (this.episodeUrl !== this.getCurrentEpisodeId)) {
        this.isBuffering = false;
        this.isPlaying = false;
      }
    },
    handlePlay() {
      this.$store.commit('audioPlayer/setProvider', this.provider);
      this.$store.commit('audioPlayer/setEpisodePermlink', this.episodePermlink);
      this.$store.commit('audioPlayer/setEpisodeTitle', this.episodeTitle);
      this.$store.commit('audioPlayer/setEpisodeUrl', this.episodeUrl);
      this.$store.commit('audioPlayer/setPodcastTitle', this.podcast ? this.podcast.name : null);
      this.$store.commit('audioPlayer/setPodcastPermlink', this.podcast ? this.podcast.permlink : null);
      this.$store.commit('audioPlayer/setSoundcloudUrl', this.soundcloudUrl);
      this.$store.commit('audioPlayer/setArtist', this.artist);
      this.$store.dispatch('audioPlayer/trigger');
      this.handleAudioPlayerListeners();
    },
  }
};
</script>
<style scoped lang="scss">
.black-circle {
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 46px;
  height: 46px;
  border-radius: 100%;
  background-color: black;
  font-size: 14px;
  margin-left: 15px;

  img {
    width: 20px;
    height: 16px;
    display: block;

    &.play {
      margin-left: 4px;
    }
  }
}
</style>
